import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, CalendarDays, Home, NotebookPen, UserRound } from 'lucide-react';
import apiClient from '../services/apiClient';
import authService from '../services/AuthService';
import { t } from '../i18n';
import AppLogo from './AppLogo';
import './AppNavigation.css';

export function showsAppNavigation(pathname) {
  if (pathname === '/vocabulary/practice') return false;
  if (pathname === '/market/publish') return false;
  return ['/', '/main', '/notes', '/calendar', '/user', '/map', '/level-up', '/tournament', '/challenge', '/triumph', '/vocabulary'].includes(pathname)
    || /^\/(library|market|study-path|vocabulary)(\/|$)/.test(pathname);
}

export function showsAppNavigationLinks(pathname) {
  return showsAppNavigation(pathname)
    && !/^\/(level-up|vocabulary|tournament|challenge|triumph)(\/|$)/.test(pathname);
}

export default function AppNavigation() {
  const { pathname } = useLocation();
  const isHomeRoute = pathname === '/' || pathname === '/main';
  const [authVersion, setAuthVersion] = useState(0);
  const userId = authService.getCurrentUserId();
  const [levelState, setLevelState] = useState(null);
  const level = levelState?.userId === userId ? levelState.level : null;

  useEffect(() => {
    const refresh = () => setAuthVersion((version) => version + 1);
    window.addEventListener('authStateChanged', refresh);
    return () => window.removeEventListener('authStateChanged', refresh);
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    // Re-entering the main screen after practice refreshes the badge; failures never imply level 0.
    Promise.resolve().then(() => apiClient.getCurrentUserSummary()).then((data) => {
      const nextLevel = Number(data?.summary?.level);
      if (!cancelled && Number.isFinite(nextLevel) && nextLevel > 0) {
        setLevelState({ userId, level: nextLevel });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, authVersion, pathname]);

  if (!userId) return null;

  const items = [
    { to: '/', key: 'home', Icon: Home, active: pathname === '/' || pathname === '/main' },
    { to: '/notes', key: 'notes', Icon: NotebookPen, active: pathname === '/notes' || pathname === '/map' },
    { to: '/calendar', key: 'activity', Icon: CalendarDays, active: pathname === '/calendar' },
    { to: '/library', key: 'library', Icon: BookOpen, active: /^\/(library|market|study-path)(\/|$)/.test(pathname) },
  ];

  return (
    <header className={`app-navigation${isHomeRoute ? ' is-home-route' : ''}`}>
      <a className="app-skip-link" href="#app-main-content">{t('interface.navigation.skip')}</a>
      <div className="app-navigation-inner">
        <div className="app-navigation-identity">
          <Link className="app-navigation-brand" to="/" aria-label={t('app.title.aria')}>
            <AppLogo />
          </Link>
          <Link className={`app-navigation-profile${pathname === '/user' ? ' is-current' : ''}`}
            to="/user" aria-current={pathname === '/user' ? 'page' : undefined}
            aria-label={level ? t('interface.navigation.profileLevel', undefined, { level })
              : t('interface.navigation.profile')}>
            <UserRound size={18} strokeWidth={1.8} aria-hidden="true" />
            <span className="app-navigation-profile-label">{t('interface.navigation.profile')}</span>
            {level && <span className="app-navigation-level" aria-hidden="true">Lv. {level}</span>}
          </Link>
        </div>
        {showsAppNavigationLinks(pathname) && <nav className="app-navigation-links" aria-label={t('interface.navigation.label')}>
          {items.map(({ to, key, Icon, active }) => (
            <Link key={key} to={to} className={`app-navigation-link${active ? ' is-current' : ''}`}
              aria-current={active ? 'page' : undefined}
              aria-label={t(`interface.navigation.${key}`)}>
              <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
              <span>{t(`interface.navigation.${key}`)}</span>
            </Link>
          ))}
        </nav>}
      </div>
    </header>
  );
}
